// The level builder's side panel, clickable (the builder has a free mouse cursor): a toolbar (select
// or place, grid, undo/redo, sky, play test), the piece palette, and a properties form for what's
// selected (type, shape, texture, colour, exact position and size, motion, booster launch) or for
// the piece about to be placed. Below it: where the piece goes, its height, the jump check.

import { SHAPES } from '../maps/boxlevel/shapes';
import type { BoxKind, LevelBox, MaterialName, Vec3Tuple } from '../maps/boxlevel/types';
import { inputName } from '../input/keybinds';
import type { EditorModel, MovePreset } from './editorModel';
import { KIND_LABEL, MOVE_PRESETS, PALETTE, PLACEABLE, THEMES } from './editorModel';
import type { EditorAction, EditorBinds } from './editorKeys';

export type EditorTool = 'select' | 'place';

export interface PanelHost {
  model(): EditorModel;
  tool(): EditorTool;
  setTool(t: EditorTool): void;
  binds(): EditorBinds;
  /** Height new pieces go at when placed in open air. */
  workZ(): number;
  setWorkZ(z: number): void;
  /** Thumbnail of a texture in a colour. */
  thumb(m: MaterialName, color: string): string;
  openCatalog(tab: string): void;
  /** The "open a map to build on" window. */
  openMaps(): void;
  playTest(): void;
  /** The side view (heights) in the corner. */
  sideView(): boolean;
  /** Music: playing, and what. */
  music(): { on: boolean; title: string | null };
  toggleMusic(): void;
  setSideView(on: boolean): void;
  /** Something changed that the 3D view should show now (tool, selection). */
  refresh(): void;
  toast(msg: string): void;
}

export const KIND_COLOR: Record<BoxKind, string> = {
  block: '#c76a2c',
  solid: '#9aa0a8',
  checkpoint: '#5ad1ff',
  start: '#4fd27a',
  end: '#ff5a5a',
  boost: '#ffd23a',
  deco: '#b48cff',
  pad: '#3affb0',
  ladder: '#c89a5a',
  water: '#2a8fc8',
  wind: '#9ad8ff',
  gravity: '#c39bff',
};

const GRIDS = [1, 2, 4, 8, 16, 32, 64, 128];

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
};

export class EditorPanel {
  private key = '';
  /** A rebuild waits while a field has focus (so typing isn't interrupted). */
  private pending = false;
  private live: HTMLElement | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly host: PanelHost,
  ) {
    root.addEventListener('focusout', () => {
      if (this.pending) setTimeout(() => this.render(), 0);
    });
    // Clicks and keys in the panel stay in the panel.
    root.addEventListener('mousedown', (e) => e.stopPropagation());
    root.addEventListener('wheel', (e) => e.stopPropagation());
  }

  /** The element the 3D view writes live info into (height, alignment, jump check). */
  get liveEl(): HTMLElement | null {
    return this.live;
  }

  private kbd(action: EditorAction): string {
    const c = this.host.binds()[action][0];
    return c ? inputName(c).replace('Page', 'Pg') : '';
  }

  /** Rebuild when anything shown changed (`force`: always). */
  render(force = false) {
    const m = this.host.model();
    const key = JSON.stringify([
      m.version,
      [...m.selection.boxes],
      [...m.selection.props],
      m.tool,
      this.host.tool(),
      this.host.workZ(),
      !!m.clipboard,
    ]);
    if (!force && key === this.key) return;
    if (this.root.contains(document.activeElement) && document.activeElement !== this.root) {
      const tag = (document.activeElement as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'SELECT') {
        this.pending = true;
        return;
      }
    }
    this.pending = false;
    this.key = key;
    const liveHtml = this.live?.innerHTML ?? '';
    this.root.textContent = '';
    this.root.append(this.header(), this.toolbar());
    const sel = m.selectedCount;
    if (sel) this.root.append(this.selectionForm());
    else if (this.host.tool() === 'place') this.root.append(this.placeForm());
    else this.root.append(this.selectHelp());
    this.live = el('section', 'live');
    this.live.innerHTML = liveHtml;
    this.root.append(this.live, this.footer());
  }

  /* ---------------- Parts ---------------- */

  private header(): HTMLElement {
    const h = el('header');
    h.append(el('b', '', 'Level builder'));
    h.append(
      this.button(
        '▶ Play test',
        () => this.host.playTest(),
        'pbtn play',
        `Play test (${this.kbd('playTest')})`,
      ),
    );
    return h;
  }

  private button(label: string, fn: () => void, cls = 'pbtn', title = ''): HTMLButtonElement {
    const b = el('button', cls, label);
    b.type = 'button';
    if (title) b.title = title;
    b.addEventListener('click', () => {
      fn();
      b.blur();
    });
    return b;
  }

  private toolbar(): HTMLElement {
    const m = this.host.model();
    const bar = el('section', 'toolbar');
    const tool = this.host.tool();
    const sel = this.button(
      'Select',
      () => this.host.setTool('select'),
      'pbtn tool' + (tool === 'select' ? ' on' : ''),
      `Select and move pieces (${this.kbd('selectTool')})`,
    );
    const place = this.button(
      'Place',
      () => this.host.setTool('place'),
      'pbtn tool' + (tool === 'place' ? ' on' : ''),
      'Place new pieces (1-7)',
    );
    const grid = el('select', 'grid');
    grid.title = `Grid size (${this.kbd('grid')})`;
    for (const g of GRIDS) grid.add(new Option('Grid ' + g, String(g)));
    grid.value = String(m.tool.grid);
    grid.addEventListener('change', () => {
      m.tool.grid = Number(grid.value);
      this.host.refresh();
      grid.blur();
    });
    const undo = this.button(
      '↶',
      () => !m.undo() && this.host.toast('Nothing to undo'),
      'pbtn icon',
      'Undo (Ctrl+Z)',
    );
    const redo = this.button(
      '↷',
      () => !m.redo() && this.host.toast('Nothing to redo'),
      'pbtn icon',
      'Redo (Ctrl+Y)',
    );
    bar.append(sel, place, grid, undo, redo);
    return bar;
  }

  private selectHelp(): HTMLElement {
    const s = el('section', 'form');
    s.append(el('h4', '', 'Select'));
    const p = el('p', 'hint');
    p.innerHTML =
      'Click a piece to select it. <b>Ctrl</b>-click adds more. Drag a selected piece to move it, ' +
      'or use the <b>arrow keys</b> and <b>PgUp / PgDn</b>.';
    s.append(p);
    return s;
  }

  /** Palette and settings for the next piece. */
  private placeForm(): HTMLElement {
    const m = this.host.model();
    const t = m.tool;
    const s = el('section', 'form');
    s.append(el('h4', '', 'Place'));
    const pal = el('div', 'palette');
    PLACEABLE.forEach((k, i) => {
      const b = this.button(
        '',
        () => {
          m.setKind(k);
          this.host.setTool('place');
        },
        'kindCard' + (t.mode === 'piece' && t.kind === k ? ' on' : ''),
        KIND_LABEL[k],
      );
      const dot = el('i');
      dot.style.background = KIND_COLOR[k];
      b.append(
        dot,
        el('span', '', KIND_LABEL[k].replace(' (no collision)', '')),
        el('kbd', '', i < 7 ? String(i + 1) : ''),
      );
      pal.append(b);
    });
    s.append(pal);
    if (t.mode !== 'piece') {
      const what =
        t.mode === 'prop'
          ? `Scenery: ${t.model.replace(/_/g, ' ')}`
          : t.mode === 'prefab'
            ? 'A ready-made obstacle'
            : 'Pasting what you copied';
      s.append(el('p', 'hint', what + '. Pick a piece above to go back to blocks.'));
    } else {
      s.append(
        this.selectRow(
          'Shape',
          SHAPES.map((x) => [x.id, x.label]),
          t.shape,
          (v) => m.setShape(v as LevelBox['shape'] & string),
        ),
        this.rotateRow(t.turn, () => m.rotate()),
        this.lookRows(
          t.material,
          t.color,
          (mat) => m.setMaterial(mat),
          (c) => m.setColor(c),
        ),
        this.vecRow('Size', ['W', 'D', 'H'], t.size, t.grid, (v) => {
          for (let k = 0; k < 3; k++) t.size[k] = Math.max(1, Math.min(4096, v[k]));
        }),
        this.selectRow(
          'Motion',
          MOVE_PRESETS.map((x) => [x.id, x.label]),
          t.move,
          (v) => (t.move = v as MovePreset),
        ),
      );
    }
    s.append(
      this.numberRow(
        'Work height',
        this.host.workZ(),
        t.grid,
        (z) => this.host.setWorkZ(z),
        `Pieces placed in open air go at this height (${this.kbd('taller')} / ${this.kbd('shorter')})`,
      ),
    );
    const more = el('div', 'rowBtns');
    more.append(
      this.button(
        'Scenery, obstacles, skies…',
        () => this.host.openCatalog('pieces'),
        'pbtn',
        `Catalog (${this.kbd('catalog')})`,
      ),
      this.button(
        'Open a map to build on…',
        () => this.host.openMaps(),
        'pbtn',
        'Copy one of our maps or a player map into Your levels and build on it',
      ),
    );
    s.append(more);
    return s;
  }

  /** Properties of the selection. */
  private selectionForm(): HTMLElement {
    const m = this.host.model();
    const s = el('section', 'form');
    const one = m.selectedBox();
    const n = m.selectedCount;
    const g = m.tool.grid;
    const edit = (fn: (b: LevelBox) => void) => {
      const err = m.editSelected(fn);
      if (err) this.host.toast(err);
      this.host.refresh();
    };
    s.append(
      el(
        'h4',
        '',
        one ? `Selected · ${KIND_LABEL[one.kind].replace(' (no collision)', '')}` : `Selected · ${n} pieces`,
      ),
    );
    const boxes = [...m.selection.boxes].map((i) => m.def.boxes[i]);
    if (boxes.length) {
      const first = boxes[0];
      const special = (b: LevelBox) => b.kind === 'start' || b.kind === 'end' || b.kind === 'checkpoint';
      s.append(
        this.selectRow(
          'Type',
          PLACEABLE.map((k) => [k, KIND_LABEL[k].replace(' (no collision)', '')]),
          boxes.every((b) => b.kind === first.kind) ? first.kind : '',
          (v) => edit((b) => (b.kind = v as BoxKind)),
        ),
      );
      if (!boxes.some(special))
        s.append(
          this.selectRow(
            'Shape',
            SHAPES.map((x) => [x.id, x.label]),
            boxes.every((b) => (b.shape ?? 'box') === (first.shape ?? 'box')) ? (first.shape ?? 'box') : '',
            (v) =>
              edit((b) => {
                if (v === 'box') delete b.shape;
                else b.shape = v as LevelBox['shape'];
              }),
          ),
          this.rotateRow(first.turn ?? 0, () =>
            edit((b) => {
              b.turn = ((b.turn ?? 0) + 1) % 4;
              // Turn the footprint with the shape.
              const cx = (b.min[0] + b.max[0]) / 2;
              const cy = (b.min[1] + b.max[1]) / 2;
              const hx = (b.max[1] - b.min[1]) / 2;
              const hy = (b.max[0] - b.min[0]) / 2;
              b.min[0] = cx - hx;
              b.max[0] = cx + hx;
              b.min[1] = cy - hy;
              b.max[1] = cy + hy;
              if (!b.turn) delete b.turn;
            }),
          ),
        );
      s.append(
        this.lookRows(
          first.material,
          first.color,
          (mat) => edit((b) => (b.material = mat)),
          (c) => edit((b) => (b.color = c)),
        ),
      );
      if (one) {
        s.append(
          this.vecRow('Position', ['X', 'Y', 'Z'], one.min, g, (v) =>
            edit((b) => {
              for (let k = 0; k < 3; k++) {
                const size = b.max[k] - b.min[k];
                b.min[k] = v[k];
                b.max[k] = v[k] + size;
              }
            }),
          ),
          this.vecRow(
            'Size',
            ['W', 'D', 'H'],
            [0, 1, 2].map((k) => one.max[k] - one.min[k]) as Vec3Tuple,
            g,
            (v) =>
              edit((b) => {
                for (let k = 0; k < 3; k++) b.max[k] = b.min[k] + Math.max(1, Math.min(8192, v[k]));
              }),
          ),
        );
        if (one.kind === 'gravity')
          s.append(
            this.numberRow('Gravity ×', one.gravity ?? 0.5, 0.1, (v) =>
              edit((b) => (b.gravity = Math.max(0.1, Math.min(3, v)))),
            ),
          );
        if ((one.kind === 'boost' || one.kind === 'pad' || one.kind === 'wind') && one.boost)
          s.append(
            this.vecRow(one.kind === 'wind' ? 'Push' : 'Launch', ['X', 'Y', 'Z'], one.boost, 50, (v) =>
              edit(
                (b) =>
                  (b.boost = [v[0], v[1], v[2]].map((x) => Math.max(-4000, Math.min(4000, x))) as Vec3Tuple),
              ),
            ),
          );
      }
      // Spinning and blinking: pieces you stand on (not fields, water or ladders).
      const still = (b: LevelBox) =>
        special(b) || b.kind === 'ladder' || b.kind === 'water' || b.kind === 'wind' || b.kind === 'gravity';
      if (one && !still(one)) {
        s.append(
          this.numberRow('Spin °/s', one.spin ?? 0, 5, (v) =>
            edit((b) => {
              const r = Math.max(-720, Math.min(720, v));
              if (r) b.spin = r;
              else delete b.spin;
            }),
          ),
          this.numberRow('Blink on (s)', one.blink?.on ?? 0, 0.1, (v) =>
            edit((b) => {
              if (v <= 0) delete b.blink;
              else b.blink = { on: Math.min(60, v), off: b.blink?.off ?? 1, phase: b.blink?.phase ?? 0 };
            }),
          ),
        );
        if (one.blink)
          s.append(
            this.numberRow('Blink off (s)', one.blink.off, 0.1, (v) =>
              edit((b) => b.blink && (b.blink.off = Math.max(0.1, Math.min(60, v)))),
            ),
          );
      }
      if (!boxes.some(special)) {
        const moving = first.move;
        const preset: MovePreset = !moving ? 'none' : moving.offset[2] !== 0 ? 'updown' : 'side';
        s.append(
          this.selectRow(
            'Motion',
            MOVE_PRESETS.map((x) => [x.id, x.label]),
            preset,
            (v) =>
              edit((b) => {
                if (v === 'none') {
                  delete b.move;
                  return;
                }
                const period = b.move?.period ?? m.tool.movePeriod;
                const d = m.tool.moveDist;
                b.move = {
                  offset: v === 'updown' ? [0, 0, -24] : v === 'forward' ? [d, 0, 0] : [0, d, 0],
                  period,
                  phase: 0,
                };
              }),
          ),
        );
        if (one && one.move) {
          s.append(
            this.vecRow('Travel', ['X', 'Y', 'Z'], one.move.offset, g, (v) =>
              edit((b) => b.move && (b.move.offset = [v[0], v[1], v[2]])),
            ),
            this.numberRow('Seconds', one.move.period, 0.2, (p) =>
              edit((b) => b.move && (b.move.period = Math.max(0.2, Math.min(600, p)))),
            ),
          );
        }
      }
    }
    const btns = el('div', 'rowBtns');
    btns.append(
      this.button(
        'Duplicate',
        () => {
          const err = m.duplicateSelection();
          if (err) this.host.toast(err);
          else this.host.toast('Duplicated: move the copy with the arrow keys or drag it');
          this.host.refresh();
        },
        'pbtn',
        'Duplicate (Ctrl+D)',
      ),
      this.button(
        'Delete',
        () => {
          const err = m.deleteSelection();
          if (err) this.host.toast(err);
          this.host.refresh();
        },
        'pbtn danger',
        'Delete (Del)',
      ),
      this.button(
        'Deselect',
        () => {
          m.selectOnly(null);
          this.host.refresh();
        },
        'pbtn',
        'Deselect (Esc)',
      ),
    );
    s.append(btns);
    s.append(
      el('p', 'hint', 'Arrow keys move it one grid step, PgUp / PgDn up and down. Shift with them resizes.'),
    );
    return s;
  }

  private footer(): HTMLElement {
    const m = this.host.model();
    const f = el('footer');
    const sky = el('select', 'sky');
    sky.title = `Sky (${this.kbd('sky')})`;
    THEMES.forEach((t, i) => sky.add(new Option(t.name + ' sky', String(i))));
    sky.value = String(Math.max(0, m.themeIndex()));
    sky.addEventListener('change', () => {
      m.setTheme(Number(sky.value));
      this.host.refresh();
      sky.blur();
    });
    const side = el('label', 'sideToggle');
    const cb = el('input');
    cb.type = 'checkbox';
    cb.checked = this.host.sideView();
    cb.addEventListener('change', () => {
      this.host.setSideView(cb.checked);
      cb.blur();
    });
    side.append(cb, ' Side view');
    side.title = 'A small view from the side, to see heights';
    const total = m.def.boxes.length + (m.def.props?.length ?? 0);
    const info = el('span', '', `${total} pieces · ${m.dirty ? 'saving…' : 'saved'}`);
    const mu = this.host.music();
    const music = this.button(
      mu.on ? `♪ ${mu.title ?? 'Music on'} · stop (M)` : '♪ Play music (M)',
      () => this.host.toggleMusic(),
      'pbtn music' + (mu.on ? ' on' : ''),
    );
    f.append(sky, info, side, music);
    const help = el('p', 'hint');
    help.innerHTML = `Hold <b>wheel button</b> to look · <b>WASD</b> fly · <b>Wheel</b> zoom · <b>Right click</b> piece menu · hold <b>Tab</b> for all keys · <b>${this.kbd('showPanel')}</b> hides this panel`;
    f.append(help);
    return f;
  }

  /* ---------------- Fields ---------------- */

  private row(label: string): [HTMLElement, HTMLElement] {
    const r = el('div', 'prow');
    r.append(el('span', 'plbl', label));
    const v = el('div', 'pval');
    r.append(v);
    return [r, v];
  }

  private selectRow(
    label: string,
    options: [string, string][],
    value: string,
    set: (v: string) => void,
  ): HTMLElement {
    const [r, v] = this.row(label);
    const s = el('select');
    if (!value) s.add(new Option('(mixed)', ''));
    for (const [id, name] of options) s.add(new Option(name, id));
    s.value = value;
    s.addEventListener('change', () => {
      if (!s.value) return;
      set(s.value);
      s.blur();
      this.host.refresh();
    });
    v.append(s);
    return r;
  }

  private rotateRow(turn: number, rotate: () => void): HTMLElement {
    const [r, v] = this.row('Rotate');
    v.append(
      this.button(
        `⟳ ${(turn % 4) * 90}°`,
        () => {
          rotate();
          this.host.refresh();
        },
        'pbtn',
        `Rotate (${this.kbd('rotate')})`,
      ),
    );
    return r;
  }

  private lookRows(
    material: MaterialName,
    color: string,
    setMat: (m: MaterialName) => void,
    setCol: (c: string) => void,
  ): HTMLElement {
    const wrap = el('div', 'look');
    const [r1, v1] = this.row('Texture');
    const tex = el('button', 'texBtn');
    tex.type = 'button';
    const img = el('img');
    img.src = this.host.thumb(material, color);
    img.alt = '';
    tex.append(img, el('span', '', material.replace('img:', '').replace(/[-_]/g, ' ')));
    tex.title = `Pick a texture (${this.kbd('texture')} cycles)`;
    tex.addEventListener('click', () => this.host.openCatalog('textures'));
    v1.append(tex);
    void setMat;
    const [r2, v2] = this.row('Colour');
    const sw = el('div', 'swatches');
    for (const c of PALETTE) {
      const b = el('button', 'swatch' + (c.toLowerCase() === color.toLowerCase() ? ' on' : ''));
      b.type = 'button';
      b.style.background = c;
      b.title = c;
      b.addEventListener('click', () => {
        setCol(c);
        this.host.refresh();
      });
      sw.append(b);
    }
    const pick = el('input');
    pick.type = 'color';
    pick.value = /^#[0-9a-f]{6}$/i.test(color) ? color : '#888888';
    pick.title = 'Any colour';
    pick.addEventListener('change', () => {
      setCol(pick.value);
      this.host.refresh();
    });
    sw.append(pick);
    v2.append(sw);
    wrap.append(r1, r2);
    return wrap;
  }

  private numberInput(value: number, step: number, set: (v: number) => void): HTMLInputElement {
    const i = el('input');
    i.type = 'number';
    i.step = String(step);
    i.value = String(Math.round(value * 100) / 100);
    i.addEventListener('change', () => {
      const v = Number(i.value);
      if (Number.isFinite(v)) set(v);
      this.host.refresh();
    });
    i.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') i.blur();
      e.stopPropagation();
    });
    return i;
  }

  private numberRow(
    label: string,
    value: number,
    step: number,
    set: (v: number) => void,
    title = '',
  ): HTMLElement {
    const [r, v] = this.row(label);
    if (title) r.title = title;
    v.append(this.numberInput(value, step, set));
    return r;
  }

  private vecRow(
    label: string,
    axes: string[],
    value: readonly number[],
    step: number,
    set: (v: Vec3Tuple) => void,
  ): HTMLElement {
    const [r, v] = this.row(label);
    v.className = 'pval vec';
    const cur: Vec3Tuple = [value[0], value[1], value[2]];
    axes.forEach((a, k) => {
      const lab = el('label', 'ax ax' + k);
      lab.append(el('span', '', a));
      lab.append(
        this.numberInput(cur[k], step, (x) => {
          cur[k] = x;
          set([...cur] as Vec3Tuple);
        }),
      );
      v.append(lab);
    });
    return r;
  }
}
